/**
 * Query key factory for map state stored in React Query cache.
 * Used for sharing map state between the persistent layout and page components
 * without needing context that would break the getLayout pattern.
 */

import type { BoundingRect, MapMarker as MapMarkerType } from '@/src/components/features/search/Map/types';
import type { AreaResponse } from '@/src/lib/api/endpoints/area';

// Re-export MapMarker for use by consumers
export type MapMarker = MapMarkerType;

export type MapState = {
  bounds: BoundingRect;
  center: { lat: number; lng: number };
  zoom: number;
  source?: MapPositionSource;
  /**
   * Identifies the area this position was seeded for so cross-area SRP
   * navigation can replace stale cached bounds while same-area SRP/HDP
   * navigation keeps the user's current pan state.
   */
  areaKey?: string;
};

export type MapPositionSource = 'url' | 'boundary' | 'map' | 'listing' | 'restore';

/**
 * A position is "live" only when it was set by a genuine user pan/zoom/border-drag
 * (source: 'map'), as opposed to one seeded from the URL, an area boundary lookup,
 * a listing selection, or a restored session. Shared by useSrpLiveSearchRequest
 * (deciding whether to use live map bounds in the request) and
 * HotpadsPageViewTracker (deciding whether a map interaction should fire the SRP
 * page-view event) so both consumers agree on what counts as a live pan.
 */
export function isLiveMapPosition(position: MapState | undefined): position is MapState & { source: 'map' } {
  return position?.source === 'map';
}

export type MapBorderState = {
  showBorder: boolean;
  isDetecting: boolean;
  encodedBoundary: string | Array<Array<string>> | null;
  isBoundaryLoading: boolean;
  boundaryAreaId?: string | null;
};

export type MapDetectedAreaState = {
  detectedArea: AreaResponse | null;
};

export type PendingAreaNavigationState = {
  targetResourceId: string;
};

export type PendingListingNavigationState = {
  sourceListingId: string;
};

export type NearMeMapContextState = {
  upgradeSlug: string;
  areaResourceId: string;
};

export type ActiveListingState = {
  listingId: string;
  lat: number;
  lng: number;
};

export const mapStateKeys = {
  all: ['mapState'] as const,
  position: () => [...mapStateKeys.all, 'position'] as const,
  markers: () => [...mapStateKeys.all, 'markers'] as const,
  border: () => [...mapStateKeys.all, 'border'] as const,
  detectedArea: () => [...mapStateKeys.all, 'detectedArea'] as const,
  pinnedMarker: () => [...mapStateKeys.all, 'pinnedMarker'] as const,
  hoveredMarker: () => [...mapStateKeys.all, 'hoveredMarker'] as const,
  activeListing: () => [...mapStateKeys.all, 'activeListing'] as const,
  pendingAreaNavigation: () => [...mapStateKeys.all, 'pendingAreaNavigation'] as const,
  pendingListingNavigation: () => [...mapStateKeys.all, 'pendingListingNavigation'] as const,
  nearMeContext: () => [...mapStateKeys.all, 'nearMeContext'] as const,
};
